import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import { Circle } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

// Rainman React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";

// Services
import AccountService from "services/accountService";
import axiosInstance from "utils/axios";

// Context
import { useMaterialUIController } from "context";

function Projects() {
  const theme = useTheme();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const navigate = useNavigate();
  
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menu, setMenu] = useState(null);
  const [connectionStatuses, setConnectionStatuses] = useState({});
  const [transferLoading, setTransferLoading] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const openMenu = ({ currentTarget }) => setMenu(currentTarget);
  const closeMenu = () => setMenu(null);

  const handleTransferToMain = async (accountName) => {
    if (accountName === "main") {
      setSnackbar({
        open: true,
        message: "Cannot transfer from main account to itself",
        severity: "warning"
      });
      return;
    }

    setTransferLoading(prev => ({ ...prev, [accountName]: true }));
    
    try {
      const response = await axiosInstance.post(`/api/v1/accounts/transfer-to-main/${accountName}`);
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: response.data.message,
          severity: "success"
        });
        // Refresh accounts data
        fetchAccounts();
      } else {
        setSnackbar({
          open: true,
          message: response.data.message || "Transfer failed",
          severity: "error"
        });
      }
    } catch (error) {
      console.error("Transfer error:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || "Transfer failed",
        severity: "error"
      });
    } finally {
      setTransferLoading(prev => ({ ...prev, [accountName]: false }));
    }
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const fetchAccounts = async () => {
    try {
      const accountsData = await AccountService.getAccountInfo();

      const enhancedAccounts = accountsData
        .map((account) => {
          const rawBalance = parseFloat(account.wallet_info?.total_wallet_balance || "0");

          // Extract the coin object for USDT
          const coins = account.wallet_info?.coins || [];
          const usdtCoin = coins.find((coin) => coin.coin === "USDT");
          const rawPnl = parseFloat(usdtCoin?.cumRealisedPnl || "0");



          return {
            ...account,
            balance: `$${rawBalance.toFixed(2)}`,
            performance: rawPnl,
          };
        })
        .sort((a, b) => a.account_name.localeCompare(b.account_name));

      setAccounts(enhancedAccounts);
      
      // Fetch connection statuses
      try {
        const statusResponse = await axiosInstance.get("api/v1/accounts/connection-status");
        setConnectionStatuses(statusResponse.data.connection_statuses || {});
      } catch (statusError) {
        console.log(`Error fetching connection statuses: ${statusError}`);
        setConnectionStatuses({});
      }
    } catch (error) {
      console.error("Error fetching accounts:", error);
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    
    fetchAccounts(); // initial fetch

    const intervalId = setInterval(() => {
      fetchAccounts(); // poll every 5 seconds
    }, 5000);

    return () => {
      isMounted = false;
      clearInterval(intervalId); // cleanup on unmount
    };
  }, []);

  // Fetch connection statuses periodically (separate from account data)
  useEffect(() => {
    const fetchConnectionStatuses = async () => {
      try {
        const statusResponse = await axiosInstance.get("api/v1/accounts/connection-status");
        setConnectionStatuses(statusResponse.data.connection_statuses || {});
      } catch (error) {
        console.log(`Error fetching connection statuses: ${error}`);
      }
    };

    // Update connection status every 15 seconds (faster than account data)
    const statusInterval = setInterval(fetchConnectionStatuses, 15000);
    
    // Cleanup interval on component unmount
    return () => clearInterval(statusInterval);
  }, []);

  const handleNavigateToAccounts = () => {
    navigate("/accounts");
    closeMenu();
  };

  const handleRefreshData = () => {
    setLoading(true);

    const fetchAccounts = async () => {
      try {
        const accountsData = await AccountService.getAccountInfo();
        const enhancedAccounts = accountsData
        .map((account) => {
          const rawBalance = parseFloat(account.wallet_info?.total_wallet_balance || "0");

          // Extract the coin object for USDT
          const coins = account.wallet_info?.coins || [];
          const usdtCoin = coins.find((coin) => coin.coin === "USDT");
          const rawPnl = parseFloat(usdtCoin?.cumRealisedPnl || "0");

          return {
            ...account,
            balance: `$${rawBalance.toFixed(2)}`,
            performance: rawPnl,
          };
        })
        .sort((a, b) => a.account_name.localeCompare(b.account_name));

        setAccounts(enhancedAccounts);
        
        // Fetch connection statuses
        try {
          const statusResponse = await axiosInstance.get("api/v1/accounts/connection-status");
          setConnectionStatuses(statusResponse.data.connection_statuses || {});
        } catch (statusError) {
          console.log(`Error fetching connection statuses: ${statusError}`);
          setConnectionStatuses({});
        }
      } catch (error) {
        console.error("Error fetching accounts:", error);
        setAccounts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
    closeMenu();
  };


  const renderMenu = (
    <Menu
      id="simple-menu"
      anchorEl={menu}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={Boolean(menu)}
      onClose={closeMenu}
      PaperProps={{
        sx: {
          backgroundColor: darkMode 
            ? theme.palette.background.paper 
            : theme.palette.background.paper,
          color: darkMode ? theme.palette.text.primary : theme.palette.text.primary,
        }
      }}
    >
      <MenuItem onClick={handleNavigateToAccounts}>View All Accounts</MenuItem>
      <MenuItem onClick={handleNavigateToAccounts}>Add New Account</MenuItem>
      <MenuItem onClick={handleRefreshData}>Refresh Data</MenuItem>
    </Menu>
  );

  // Helper function to render connection status indicator
  const renderConnectionStatus = (accountName) => {
    const status = connectionStatuses[accountName];
    let color = "#f44336"; // Red for disconnected/error
    let title = "Disconnected";
    
    if (status) {
      switch (status.status) {
        case "connected":
          color = "#4caf50"; // Green for connected
          title = `Connected (Last ping: ${status.last_ping ? new Date(status.last_ping).toLocaleString() : 'N/A'})`;
          break;
        case "disconnected":
          color = "#f44336"; // Red for disconnected
          title = "Disconnected - Check connection or click Fix Me";
          break;
        case "error":
          color = "#ff9800"; // Orange for error
          title = "Invalid API credentials - Update credentials and click Fix Me";
          break;
        default:
          color = "#9e9e9e"; // Gray for unknown
          title = "Unknown Status - Click Fix Me to reconnect";
      }
    }
    
    return (
      <Circle 
        sx={{ 
          color: color, 
          fontSize: "12px",
          cursor: "help"
        }}
        title={title}
      />
    );
  };

  const data = {
    columns: [
      { Header: "Account", accessor: "account", width: "30%", align: "left" },
      { Header: "Status", accessor: "status", width: "10%", align: "center" },
      { Header: "Balance", accessor: "balance", align: "center" },
      { Header: "Total PnL (USDT)", accessor: "performance", align: "center" },
      { Header: "Actions", accessor: "actions", width: "15%", align: "center" },
    ],
    rows: accounts.map((account) => {
      const isPositive = account.performance >= 0;
      
      return {
        account: (
          <MDBox display="flex" alignItems="center" lineHeight={1}>
            <Icon
              sx={{
                fontWeight: "bold",
                color: ({ palette: { info } }) => info.main,
                mr: 1,
              }}
            >
              account_circle
            </Icon>
            <MDTypography
              variant="button"
              fontWeight="medium"
              lineHeight={1}
              color={darkMode ? "white" : "text"}
            >
              {account.account_name}
            </MDTypography>
          </MDBox>
        ),
        status: (
          <MDBox display="flex" justifyContent="center" alignItems="center">
            {renderConnectionStatus(account.account_name)}
          </MDBox>
        ),
        balance: (
          <MDTypography 
            variant="caption" 
            color={darkMode ? "white" : "text"} 
            fontWeight="medium"
          >
            {account.balance}
          </MDTypography>
        ),
        performance: (
          <MDTypography
            variant="caption"
            fontWeight="medium"
            color={isPositive ? "success" : "error"}
          >
            {account.performance.toFixed(2)}
          </MDTypography>
        ),
        actions: (
          <MDBox display="flex" justifyContent="center" alignItems="center">
            {account.role && account.role.includes("sub") && (
              <Tooltip title="Transfer free balance to main account">
                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTransferToMain(account.account_name);
                  }}
                  disabled={transferLoading[account.account_name]}
                  startIcon={
                    transferLoading[account.account_name] ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Icon>account_balance_wallet</Icon>
                    )
                  }
                >
                  {transferLoading[account.account_name] ? "Transferring..." : "Transfer"}
                </Button>
              </Tooltip>
            )}
          </MDBox>
        ),
        account_name: account.account_name, // For row click
      };
    }),
  };

  const handleRowClick = (rowData) => {
    if (rowData && rowData.account_name) {
      navigate(`/account-details/${rowData.account_name}`);
    }
  };

  return (
    <Card
      sx={{
        backgroundColor: darkMode 
          ? theme.palette.background.card 
          : theme.palette.background.paper,
      }}
    >
      <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
        <MDBox>
          <MDTypography 
            variant="h6" 
            gutterBottom
            color={darkMode ? "white" : "text"}
          >
            Accounts
          </MDTypography>
          <MDBox display="flex" alignItems="center" lineHeight={0}>
            <Icon
              sx={{ fontWeight: "bold", color: ({ palette: { info } }) => info.main, mt: -0.5 }}
            >
              done
            </Icon>
            <MDTypography 
              variant="button" 
              fontWeight="regular" 
              color={darkMode ? "white" : "text"}
            >
              &nbsp;<strong>{accounts.length} accounts</strong> traded
            </MDTypography>
          </MDBox>
        </MDBox>
        <MDBox color={darkMode ? "white" : "text"} px={2}>
          <Icon
            sx={{ 
              cursor: "pointer", 
              fontWeight: "bold",
              color: darkMode ? theme.palette.text.primary : theme.palette.text.primary,
            }}
            fontSize="small"
            onClick={openMenu}
          >
            more_vert
          </Icon>
        </MDBox>
        {renderMenu}
      </MDBox>
      <MDBox>
        {loading ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" p={3}>
            <CircularProgress color="info" />
          </MDBox>
        ) : (
          <DataTable
            table={data}
            showTotalEntries={false}
            isSorted={false}
            noEndBorder
            entriesPerPage={false}
            canSearch={false}
            onRowClick={handleRowClick}
          />
        )}
      </MDBox>
      
      {/* Snackbar for transfer notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
}

export default Projects;